// src/lib/mount.ts
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import * as Components from '../components'; // Assuming components are exported from components/index.ts
import { log } from './log';

// Keep track of mounted roots to unmount them later
const mountedRoots = new Map<Element, Root>();

// Define the props your components might accept
// This should be more specific based on your actual components
type ComponentProps = Record<string, any>;

// Define which components are available for mounting
// Use keyof typeof Components for type safety
type MountableComponentType = keyof typeof Components;

/**
 * Mounts a specified library component into a target DOM element.
 * @param targetElementOrId The DOM element or its ID where the component should be mounted.
 * @param componentName The name of the component to mount (must match an export from src/components).
 * @param props The props to pass to the React component.
 * @returns True if mounting was successful, false otherwise.
 */
export function mountComponent(
  targetElementOrId: string | Element,
  componentName: MountableComponentType,
  props: ComponentProps = {},
): boolean {
  const targetElement = typeof targetElementOrId === 'string'
    ? findOrCreateElement(targetElementOrId)
    : targetElementOrId;

  if (!targetElement) {
    log('error', `Target element "${targetElementOrId}" not found.`);
    return false;
  }

  // Check if the component exists in our exports
  const ComponentToMount = Components[componentName];
  if (!ComponentToMount) {
    log('error', `Component "${componentName}" not found.`);
    return false;
  }

  // Unmount existing component in the same container if present
  unmountComponent(targetElement);

  // Create a new React root and render the component
  const root = createRoot(targetElement);
  root.render(React.createElement(ComponentToMount, props as any));
  mountedRoots.set(targetElement, root);

  log('info', `Component "${componentName}" mounted successfully.`);
  return true;
}

/**
 * Unmounts a library component from a target DOM element.
 * @param targetElementOrId The DOM element or its ID where the component was mounted.
 * @returns True if unmounting was successful, false otherwise.
 */
export function unmountComponent(targetElementOrId: string | Element): boolean {
  const targetElement = typeof targetElementOrId === 'string'
    ? document.getElementById(targetElementOrId)
    : targetElementOrId;

  if (!targetElement) {
    // Don't error if element not found during unmount, might already be gone
    return false;
  }

  const root = mountedRoots.get(targetElement);
  if (root) {
    root.unmount();
    mountedRoots.delete(targetElement);
    log('info', `Component unmounted successfully from element.`);
    return true;
  }
  return false; // Nothing was mounted there by this library
}

// Optional: Function to unmount all components managed by the library
export function unmountAllComponents() {
  mountedRoots.forEach((root, element) => {
    root.unmount();
    mountedRoots.delete(element);
  });
  log('info', `All managed components unmounted.`);
}

export function findOrCreateElement(targetId: string): HTMLElement {
  const el = document.getElementById(targetId)
  if (el) return el;

  const newEl = document.createElement('div', { is: 'div' })
  newEl.id = targetId
  document.body.append(newEl)
  return newEl
}
